import { useState, useEffect, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { useAuthStore } from '@/stores/authStore'
import { supabase } from '@/lib/supabase'
import { format } from 'date-fns'
import { createDogNameMap } from '@/lib/dogNames'
import type { Event, EventType } from '@/types/database'

const eventTypes: EventType[] = [
  'pageview',
  'click',
  'scroll',
  'form_start',
  'form_submit',
  'form_abandon',
  'rage_click',
  'visibility_change',
]

interface EventWithSession extends Event {
  sessions?: {
    visitor_hash: string
  } | null
}

type SortKey = 'timestamp' | 'event_type' | 'path' | 'page_title'
type SortDirection = 'asc' | 'desc'

export default function ExplorerPage() {
  const { site } = useAuthStore()
  const [events, setEvents] = useState<EventWithSession[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [totalCount, setTotalCount] = useState(0)
  const [sortConfig, setSortConfig] = useState<{ key: SortKey; direction: SortDirection }>({
    key: 'timestamp',
    direction: 'desc',
  })
  const [filters, setFilters] = useState({
    eventTypes: [] as EventType[],
    path: '',
    title: '',
    dateFrom: '',
    dateTo: '',
  })
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set())
  const limit = 50

  useEffect(() => {
    if (site) {
      fetchEvents()
    }
  }, [site, page, filters, sortConfig])

  const fetchEvents = async () => {
    if (!site) return
    setIsLoading(true)

    try {
      // Build base query
      let countQuery = supabase
        .from('events')
        .select('*', { count: 'exact', head: true })
        .eq('site_id', site.id)

      let dataQuery = supabase
        .from('events')
        .select('*, sessions(visitor_hash)')
        .eq('site_id', site.id)

      // Apply filters
      if (filters.eventTypes.length > 0) {
        countQuery = countQuery.in('event_type', filters.eventTypes)
        dataQuery = dataQuery.in('event_type', filters.eventTypes)
      }

      if (filters.path) {
        countQuery = countQuery.ilike('path', `%${filters.path}%`)
        dataQuery = dataQuery.ilike('path', `%${filters.path}%`)
      }

      if (filters.title) {
        countQuery = countQuery.ilike('page_title', `%${filters.title}%`)
        dataQuery = dataQuery.ilike('page_title', `%${filters.title}%`)
      }

      if (filters.dateFrom) {
        const fromDate = new Date(filters.dateFrom).toISOString()
        countQuery = countQuery.gte('timestamp', fromDate)
        dataQuery = dataQuery.gte('timestamp', fromDate)
      }

      if (filters.dateTo) {
        const toDate = new Date(filters.dateTo + 'T23:59:59').toISOString()
        countQuery = countQuery.lte('timestamp', toDate)
        dataQuery = dataQuery.lte('timestamp', toDate)
      }

      // Get count
      const { count } = await countQuery
      setTotalCount(count || 0)
      setTotalPages(Math.ceil((count || 0) / limit))

      // Apply sorting and pagination
      dataQuery = dataQuery.order(sortConfig.key, { ascending: sortConfig.direction === 'asc' })
      dataQuery = dataQuery.range((page - 1) * limit, page * limit - 1)

      const { data } = await dataQuery

      setEvents((data || []) as EventWithSession[])
    } catch (error) {
      console.error('Failed to fetch events:', error)
    } finally {
      setIsLoading(false)
    }
  }

  // Create dog name map for current events
  const dogNameMap = useMemo(() => {
    const hashes = events
      .map(e => e.sessions?.visitor_hash)
      .filter((h): h is string => !!h)
    return createDogNameMap(hashes)
  }, [events])

  const handleSort = (key: SortKey) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'desc' ? 'asc' : 'desc',
    }))
    setPage(1)
  }

  const toggleEventType = (type: EventType) => {
    setFilters(prev => ({
      ...prev,
      eventTypes: prev.eventTypes.includes(type)
        ? prev.eventTypes.filter(t => t !== type)
        : [...prev.eventTypes, type],
    }))
    setPage(1)
  }

  const toggleRowExpand = (id: string) => {
    setExpandedRows(prev => {
      const next = new Set(prev)
      if (next.has(id)) {
        next.delete(id)
      } else {
        next.add(id)
      }
      return next
    })
  }

  const clearFilters = () => {
    setFilters({ eventTypes: [], path: '', title: '', dateFrom: '', dateTo: '' })
    setPage(1)
  }

  const hasFilters = filters.eventTypes.length > 0 || filters.path || filters.title || filters.dateFrom || filters.dateTo

  const exportCSV = () => {
    if (events.length === 0) return

    const headers = ['Timestamp', 'Type', 'Path', 'Page Title', 'Session', 'Visitor', 'Properties']
    const rows = events.map(e => [
      e.timestamp,
      e.event_type,
      e.path || '',
      e.page_title || '',
      e.session_id,
      dogNameMap.get(e.sessions?.visitor_hash || '') || e.sessions?.visitor_hash || '',
      JSON.stringify(e.properties || {}),
    ])

    const csv = [headers, ...rows].map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(',')).join('\n')
    const blob = new Blob([csv], { type: 'text/csv' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `events-${format(new Date(), 'yyyy-MM-dd')}.csv`
    a.click()
    URL.revokeObjectURL(url)
  }

  const SortIcon = ({ column }: { column: SortKey }) => {
    if (sortConfig.key !== column) {
      return (
        <svg className="w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
        </svg>
      )
    }
    return sortConfig.direction === 'desc' ? (
      <svg className="w-4 h-4 text-primary-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
      </svg>
    ) : (
      <svg className="w-4 h-4 text-primary-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l7-7m0 0l7 7m-7-7v18" />
      </svg>
    )
  }

  if (!site) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px]">
        <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
          <svg className="w-8 h-8 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
          </svg>
        </div>
        <h2 className="text-xl font-semibold text-gray-900 mb-2">No site configured</h2>
        <p className="text-gray-600 mb-4">Add your website to start tracking analytics.</p>
        <Link to="/settings" className="btn-primary">Add your site</Link>
      </div>
    )
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Data Explorer</h1>
          <p className="text-gray-600">Browse and export raw event data</p>
        </div>
        <button onClick={exportCSV} disabled={events.length === 0} className="btn-secondary">
          Export CSV
        </button>
      </div>

      {/* Filters */}
      <div className="card mb-6">
        <div className="space-y-4">
          {/* Event type checkboxes */}
          <div>
            <label className="label mb-2 block">Event Types</label>
            <div className="flex flex-wrap gap-2">
              {eventTypes.map((type) => (
                <button
                  key={type}
                  onClick={() => toggleEventType(type)}
                  className={`px-3 py-1.5 text-sm rounded-lg border transition-colors ${
                    filters.eventTypes.includes(type)
                      ? 'bg-primary-100 border-primary-300 text-primary-700'
                      : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  {type.replace('_', ' ')}
                </button>
              ))}
            </div>
          </div>

          {/* Text and date filters */}
          <div className="flex flex-wrap gap-4">
            <div>
              <label className="label">Path contains</label>
              <input
                type="text"
                value={filters.path}
                onChange={(e) => {
                  setFilters({ ...filters, path: e.target.value })
                  setPage(1)
                }}
                placeholder="/blog"
                className="input"
              />
            </div>
            <div>
              <label className="label">Title contains</label>
              <input
                type="text"
                value={filters.title}
                onChange={(e) => {
                  setFilters({ ...filters, title: e.target.value })
                  setPage(1)
                }}
                placeholder="Home"
                className="input"
              />
            </div>
            <div>
              <label className="label">From date</label>
              <input
                type="date"
                value={filters.dateFrom}
                onChange={(e) => {
                  setFilters({ ...filters, dateFrom: e.target.value })
                  setPage(1)
                }}
                className="input"
              />
            </div>
            <div>
              <label className="label">To date</label>
              <input
                type="date"
                value={filters.dateTo}
                onChange={(e) => {
                  setFilters({ ...filters, dateTo: e.target.value })
                  setPage(1)
                }}
                className="input"
              />
            </div>
            {hasFilters && (
              <div className="flex items-end">
                <button onClick={clearFilters} className="btn-secondary">
                  Clear filters
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Results count */}
      {!isLoading && (
        <div className="mb-4 text-sm text-gray-600">
          {totalCount.toLocaleString()} events found
        </div>
      )}

      {isLoading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
        </div>
      ) : events.length === 0 ? (
        <div className="card text-center py-12">
          <p className="text-gray-600">No events found</p>
          {hasFilters && (
            <p className="text-sm text-gray-500 mt-1">Try adjusting your filters</p>
          )}
        </div>
      ) : (
        <>
          <div className="card overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-gray-500 border-b bg-gray-50">
                  <th className="px-4 py-3 font-medium">
                    <button
                      onClick={() => handleSort('timestamp')}
                      className="flex items-center gap-1 hover:text-gray-700"
                    >
                      Timestamp
                      <SortIcon column="timestamp" />
                    </button>
                  </th>
                  <th className="px-4 py-3 font-medium">
                    <button
                      onClick={() => handleSort('event_type')}
                      className="flex items-center gap-1 hover:text-gray-700"
                    >
                      Type
                      <SortIcon column="event_type" />
                    </button>
                  </th>
                  <th className="px-4 py-3 font-medium">
                    <button
                      onClick={() => handleSort('path')}
                      className="flex items-center gap-1 hover:text-gray-700"
                    >
                      Path
                      <SortIcon column="path" />
                    </button>
                  </th>
                  <th className="px-4 py-3 font-medium">
                    <button
                      onClick={() => handleSort('page_title')}
                      className="flex items-center gap-1 hover:text-gray-700"
                    >
                      Page Title
                      <SortIcon column="page_title" />
                    </button>
                  </th>
                  <th className="px-4 py-3 font-medium">Visitor</th>
                  <th className="px-4 py-3 font-medium">Properties</th>
                </tr>
              </thead>
              <tbody>
                {events.map((event) => {
                  const isExpanded = expandedRows.has(event.id)
                  const hasProperties = event.properties && Object.keys(event.properties as object).length > 0
                  const visitorHash = event.sessions?.visitor_hash
                  const dogName = visitorHash ? dogNameMap.get(visitorHash) : null

                  return (
                    <tr key={event.id} className="border-b last:border-0 hover:bg-gray-50">
                      <td className="px-4 py-3 text-gray-600 whitespace-nowrap">
                        {format(new Date(event.timestamp), 'MMM d, HH:mm:ss')}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`badge ${
                          event.event_type === 'pageview' ? 'badge-success' :
                          event.event_type === 'click' ? 'badge-info' :
                          event.event_type === 'form_submit' ? 'badge-warning' :
                          event.event_type === 'rage_click' ? 'badge-danger' :
                          'badge-gray'
                        }`}>
                          {event.event_type.replace('_', ' ')}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-gray-900 max-w-[200px] truncate" title={event.path || undefined}>
                        {event.path || '-'}
                      </td>
                      <td className="px-4 py-3 text-gray-600 max-w-[200px] truncate" title={event.page_title || undefined}>
                        {event.page_title || '-'}
                      </td>
                      <td className="px-4 py-3">
                        <Link
                          to={`/sessions/${event.session_id}`}
                          className="text-primary-600 hover:underline font-medium"
                        >
                          {dogName || (visitorHash ? visitorHash.slice(0, 8) : event.session_id.slice(0, 8))}
                        </Link>
                      </td>
                      <td className="px-4 py-3">
                        {hasProperties ? (
                          <div>
                            <button
                              onClick={() => toggleRowExpand(event.id)}
                              className="text-primary-600 hover:text-primary-700 text-xs font-medium"
                            >
                              {isExpanded ? 'Hide' : 'Show'}
                            </button>
                            {isExpanded && (
                              <div className="mt-2 text-xs text-gray-500 bg-gray-100 rounded p-2 max-w-md">
                                <pre className="whitespace-pre-wrap break-all">
                                  {JSON.stringify(event.properties, null, 2)}
                                </pre>
                              </div>
                            )}
                          </div>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-4">
              <p className="text-sm text-gray-600">
                Page {page} of {totalPages}
              </p>
              <div className="flex gap-2">
                <button
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="btn-secondary px-3 py-1.5 text-sm disabled:opacity-50"
                >
                  Previous
                </button>
                <button
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  className="btn-secondary px-3 py-1.5 text-sm disabled:opacity-50"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  )
}
